import Map from 'ol/Map';
import View from 'ol/View';
import Feature from 'ol/Feature';
import { Tile as TileLayer, VectorTile as VectorTileLayer } from 'ol/layer';
import { Vector as VectorLayer, Group as LayerGroup } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import { GeometryCollection, LineString, Point, Polygon } from 'ol/geom';
import { Circle, Fill, Text, Stroke, Style, Icon } from 'ol/style';
import { Draw, Modify, Snap } from 'ol/interaction';
import WKT from 'ol/format/WKT';
import { get as getProjection, getTransform } from 'ol/proj';
import Projection from 'ol/proj/Projection';
import { fromLonLat, toLonLat, transform } from 'ol/proj';
import Overlay from 'ol/Overlay';
import TileWMS from 'ol/source/TileWMS';
import TileGrid from 'ol/tilegrid/TileGrid';
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { getWidth, getTopLeft, buffer } from 'ol/extent';
import { ref, reactive } from 'vue';
import { useDefaultsListStore } from '@/stores/defaultsListStore';
import { storeToRefs } from 'pinia';
import defaultTileLayers from '@/config/defaultTileLayers.js';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import CircleStyle from 'ol/style/Circle';
import { ForestreeIconFactory } from '@/config/ForestreeIconFactory.js';
import { GeoJSON } from 'ol/format';
import XYZ from 'ol/source/XYZ';

const useMapFilter = () => {
    const map = ref({});
    const mapCreated = ref(false);
    const overlay = ref(null);
    const container = ref(null);
    const content = ref(null);
    const closer = ref(null);
    const filterTreesSource = ref(new VectorSource());
    const filterTreesLayer = ref(new VectorLayer({ source: filterTreesSource.value }));
    const selectionSource = ref(new VectorSource());
    const selectionLayer = ref(new VectorLayer());
    const selectedFeatures = ref([]);
    const polyGeom = ref(null);
    const polygonSource = ref(new VectorSource());
    const polygonVector = ref(new VectorLayer());
    const modify = ref(null);
    const snap = ref(null);
    const draw = ref(null);
    const polygonAdded = ref(false);
    const polygonWkt = ref(null);
    const aerialGroup = ref(null);
    const imageService = ref(new TileLayer({ visible: false }));
    const imageService2 = ref(new TileLayer({ visible: false }));
    const satellite = ref(defaultTileLayers().satelliteTileLayer);
    const osm = ref(defaultTileLayers().osmTileLayer);
    const labels = ref(defaultTileLayers().labelsTileLayer);
    const aerial = ref(defaultTileLayers().aerialTileLayer);

    const defaultsListStore = useDefaultsListStore();
    const { homeCenter, defaults } = storeToRefs(defaultsListStore);
    let styleCache = null;
    const createMap = (hasPopup = false) => {
        const homeLng = parseFloat(homeCenter?.value?.lng || 0);
        const homeLat = parseFloat(homeCenter?.value?.lat || 0);
        const homeZoom = parseFloat(homeCenter?.value?.zoom || 0);
        map.value = new Map({
            target: 'map',
            layers: [osm.value, satellite.value],
            view: new View({
                projection: 'EPSG:3857',
                center: fromLonLat([homeLng, homeLat], 'EPSG:3857'),
                zoom: homeZoom
            })
        });

        if (hasPopup) {
            container.value = document.getElementById('popup');
            content.value = document.getElementById('popup-content');
            closer.value = document.getElementById('popup-closer');

            overlay.value = new Overlay({
                element: container.value,
                autoPan: {
                    animation: { duration: 250 }
                }
            });

            map.value.addOverlay(overlay.value);

            closer.value.onclick = () => {
                overlay.value.setPosition(undefined);
                closer.value.blur();
                return false;
            };
        } else {
            overlay.value = null;
        }

        mapCreated.value = true;

        polygonSource.value = new VectorSource();

        polygonVector.value = new VectorLayer({
            source: polygonSource.value,
            style: new Style({
                fill: new Fill({ color: 'rgba(255, 255, 255, 0.1)' }),
                stroke: new Stroke({ color: '#dd6b20', width: 3 }),
                image: new Circle({ radius: 7, fill: new Fill({ color: '#dd6b20' }) })
            })
        });

        map.value.addLayer(polygonVector.value);

        selectionSource.value = new VectorSource();

        selectionLayer.value = new VectorLayer({
            source: selectionSource.value,
            style: new Style({
                image: new Circle({
                    radius: 9,
                    fill: new Fill({ color: 'rgba(254, 240, 138, 0.2)' }),
                    stroke: new Stroke({ color: 'rgba(254, 240, 138, 1)', width: 4 })
                })
            })
        });

        map.value.addLayer(selectionLayer.value);
        selectionLayer.value.setZIndex(20);

        // modify.value = new Modify({ source: polygonSource.value });
        // draw.value = new Draw({ source: polygonSource.value, type: 'Polygon' });
        // snap.value = new Snap({ source: polygonSource.value });

        const predefinedStyles = {
            'circles-zoom': {
                symbol: {
                    symbolType: 'circle',
                    size: ['interpolate', ['exponential', 2.5], ['zoom'], 2, 8, 20, 32],
                    color: [
                        'interpolate',
                        ['linear'],
                        ['get', 'priority'],
                        1,
                        '#16a34a',
                        2,
                        '#ca8a04',
                        3,
                        '#ef4444',
                        4,
                        '#b91c1c'
                    ],
                    offset: [0, 0],
                    opacity: 0.7
                }
            }
        };

        filterTreesLayer.value = new WebGLPointsLayer({
            source: filterTreesSource.value,
            //style: predefinedStyles['circles-zoom'],
            style: {
                'circle-radius': [
                    'interpolate',
                    ['exponential', 2.5],
                    ['zoom'],
                    2, 7,
                    20, 32
                ],
                'circle-fill-color': ['match', ['get', 'priority'],
                    1, 'rgba(22, 163, 74, 0.7)',
                    2, 'rgba(202, 138, 4, 0.7)',
                    3, 'rgba(239, 68, 68, 0.7)',
                    4, 'rgba(185, 28, 28, 0.7)',
                    'rgba(22, 163, 74, 0.7)'
                ],
                'circle-displacement': [0, 0],
                'circle-opacity': 0.9
            },
            disableHitDetection: false
        });

        map.value.addLayer(filterTreesLayer.value);
        // addImagery();
    };

    function enablePolygonDraw () {
        // Step 1: Remove interactions first
        map.value.removeInteraction(modify.value);
        map.value.removeInteraction(draw.value);
        map.value.removeInteraction(snap.value);
    
        clearFeatures();

        modify.value = new Modify({ source: polygonSource.value });
        draw.value = new Draw({ source: polygonSource.value, type: 'Polygon' });
        snap.value = new Snap({ source: polygonSource.value });

        map.value.addInteraction(modify.value);
        map.value.addInteraction(draw.value);
        map.value.addInteraction(snap.value);
        const format = new WKT();

        draw.value.on('drawend', (e) => {
            map.value.removeInteraction(draw.value);
            polygonAdded.value = true;
            const geom = e.feature.getGeometry();
            polygonWkt.value = format.writeGeometry(geom);
            polyGeom.value = e.feature.getGeometry();
            selectFeatures();
        });

        modify.value.on('modifyend', (e) => {
            polygonWkt.value = format.writeGeometry(e.features.getArray()[0].getGeometry());
            polyGeom.value = e.features.getArray()[0].getGeometry();
            selectFeatures();
        });
    }

    function selectFeatures () {
        selectedFeatures.value = [];
        selectionSource.value.clear();
        const features = filterTreesSource.value.getFeatures();
        for (let i = 0; i < features.length; i++) {
            if (polyGeom.value.intersectsExtent(features[i].getGeometry().getExtent())) {
                selectedFeatures.value.push({
                    mid: features[i].get('mid'),
                    tree_id: features[i].get('tree_id')
                });
                selectionSource.value.addFeature(features[i]);
            }
        }
        map.value.updateSize();
    }

    function updateWkt () {
        var format = new WKT();
        var geomAll = polygonSource.value.getFeatures();
        let geomC = new GeometryCollection(geomAll.map((f) => f.getGeometry()));
        if (format.writeGeometry(geomC) === 'GEOMETRYCOLLECTION EMPTY') {
            polygonWkt.value = null;
        } else {
            polygonWkt.value = format.writeGeometry(geomC);
        }
    }

    function addFeatureToMap (feature, selectFeaturesOnMap = false) {
        polygonSource.value.addFeature(new WKT().readFeature(feature));
        updateWkt();
        if (selectFeaturesOnMap) {
            const thisFeature = new WKT().readFeature(feature);
            polygonAdded.value = true;
            polygonWkt.value = feature;
            polyGeom.value = thisFeature.getGeometry();
            selectFeatures();
        }
    }

    function restart () {
        // Step 1: Remove interactions first
        map.value.removeInteraction(modify.value);
        map.value.removeInteraction(draw.value);
        map.value.removeInteraction(snap.value);
    
        clearFeatures();
    
        enablePolygonDraw();
    }

    function clearFeatures () {
        polygonSource.value.clear();
        selectionSource.value.clear();
        selectedFeatures.value = [];
        polygonWkt.value = null;
        polyGeom.value = null;
        polygonAdded.value = false;
    }

    function addTreesGL (trees) {
        if (!map.value || !mapCreated.value) {
            console.log('Map not initialized');
            return;
        }

        if (trees && trees.length > 0) {
            filterTreesSource.value.clear();
            const features = trees.map((t) => {
                const lat = t.tree ? t.tree.lat : t.lat;
                const lng = t.tree ? t.tree.lng : t.lng;
                return new Feature({
                    id: t.tree ? t.tree.id : t.id,
                    tree_id: t.tree ? t.tree.id : t.id,
                    mid: t.id ? t.id : NULL,
                    priority: t.inspection ? t.inspection.priority : 1,
                    geometry: new Point(fromLonLat([parseFloat(lng), parseFloat(lat)], 'EPSG:3857'))
                });
            });
            filterTreesSource.value.addFeatures(features);
            // Force a refresh of the map view
            const view = map.value.getView();
            view.changed();
        } else {
            filterTreesSource.value.clear();
        }

        if (polyGeom.value) {
            selectFeatures();
        } else {
            selectedFeatures.value = [];
            polyGeom.value = null;
        }
    }

    function baseMapSwitch () {
        if (osm.value.getVisible()) {
            if (defaults.value.imagery) {
                imageService.value.setVisible(true);
                imageService2.value.setVisible(true);
                labels.value.setVisible(true);
            } else if (defaults.value.homeCenter.tile) {
                aerial.value.setVisible(true);
                labels.value.setVisible(true);
            } else {
                satellite.value.setVisible(true);
                labels.value.setVisible(true);
            }
            osm.value.setVisible(false);
        } else {
            if (defaults.value.imagery) {
                imageService.value.setVisible(false);
                imageService2.value.setVisible(false);
                labels.value.setVisible(false);
            } else if (defaults.value.homeCenter.tile) {
                aerial.value.setVisible(false);
                labels.value.setVisible(false);
            } else {
                satellite.value.setVisible(false);
                labels.value.setVisible(false);
            }
            osm.value.setVisible(true);
        }
    }

    function addImagery () {

        if (defaults.value.imagery) {

            let showAerial = localStorage.getItem('aerial');

            if (showAerial === 'true') {
                osm.value.setVisible(false);
            }

            let imageUrl = '';
            let source = null;
            let source2 = null;
            let visState = false;

            if (defaults.value.imagery.provider === 'metromap') {
                imageUrl = `https://pathapi.metromap.com.au/api-link/${defaults.value.imagery.key}/wmts/Australian_capital_cities_latest/default/GoogleMapsCompatibleExt:epsg:3857/{z}/{y}/{x}.jpgpng`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
            }

            if (defaults.value.imagery.provider === 'metromap-xyz') {
                imageUrl = `https://api-v3.metromap.com.au/tiles/xyz/Australian_capital_cities_latest/{z}/{y}/{x}.jpgpng?key=${defaults.value.imagery.key}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
            }

            if (defaults.value.imagery.provider === 'metromap-wmts') {
                const projection = getProjection('EPSG:3857');
                const projectionExtent = projection.getExtent();
                const size = getWidth(projectionExtent) / 256;
                const resolutions = new Array(22);
                const matrixIds = new Array(22);
                for (let z = 0; z < 22; ++z) {
                    resolutions[z] = size / Math.pow(2, z);
                    matrixIds[z] = z;
                }

                source = new WMTS({
                    url: `https://api.metromap.com.au/ogc/key/${defaults.value.imagery.key}/service?SERVICE=WMTS`,
                    layer: 'Australian_capital_cities_latest',
                    matrixSet: 'webmercator',
                    format: 'image/png',
                    projection: projection,
                    tileGrid: new WMTSTileGrid({
                        origin: getTopLeft(projectionExtent),
                        resolutions: resolutions,
                        matrixIds: matrixIds
                    }),
                    style: 'default',
                    wrapX: true
                });
            }

            if (defaults.value.imagery.provider === 'nearmap') {
                imageUrl = `https://au0.nearmap.com/maps/z={z}&x={x}&y={y}&version=2&nml=Vert&client=wmts_integration&httpauth=false&apikey=${defaults.value.imagery.key}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tilePixelRatio: 1
                });
            }

            if (defaults.value.imagery.provider === 'esri' && defaults.value.imagery.url) {
                imageUrl = `${defaults.value.imagery.url}/tile/{z}/{y}/{x}`;
                source = new XYZ({
                    urls: [imageUrl],
                    tileSize: 512,
                    maxZoom: 19
                });
            }

            if (defaults.value.imagery.provider === 'victoria') {
                const projExtent = getProjection('EPSG:3857').getExtent();
                const startResolution = getWidth(projExtent) / 512;
                const resolutions = new Array(22);
                for (let i = 0, ii = resolutions.length; i < ii; ++i) {
                    resolutions[i] = startResolution / Math.pow(2, i);
                }
                const tileGrid = new TileGrid({
                    extent: projExtent,
                    resolutions: resolutions,
                    tileSize: [512, 512]
                });

                source = new TileWMS({
                    url: 'https://base.maps.vic.gov.au/service?',
                    params: { 'LAYERS': 'AERIAL_WM', 'TILED': true },
                    transition: 0,
                    tileGrid: tileGrid
                });
            }

            if (defaults.value.imagery.provider === 'dual-wms') {
                const projExtent = getProjection('EPSG:3857').getExtent();
                const startResolution = getWidth(projExtent) / 512;
                const resolutions = new Array(22);
                for (let i = 0, ii = resolutions.length; i < ii; ++i) {
                    resolutions[i] = startResolution / Math.pow(2, i);
                }
                const tileGrid = new TileGrid({
                    extent: projExtent,
                    resolutions: resolutions,
                    tileSize: [512, 512]
                });

                source = new TileWMS({
                    url: defaults.value.imagery.url,
                    transition: 0,
                    tileGrid: tileGrid,
                    format: 'image/png'
                });

                source2 = new TileWMS({
                    url: defaults.value.imagery.url2,
                    transition: 0,
                    tileGrid: tileGrid
                });
            }

            imageService.value = new TileLayer({
                source: source,
                visible: visState
            });

            imageService2.value = new TileLayer({
                source: source2,
                visible: visState,
                crossOrigin: 'anonymous'
            });

            aerialGroup.value = new LayerGroup({
                layers: [
                    imageService2.value,
                    imageService.value,
                    labels.value
                ]
            });

            labels.value.setVisible(visState);

            const layers = map.value.getLayers();
            layers.insertAt(0, aerialGroup.value);
        } else if (defaults.value?.homeCenter?.tile) {

            let visState = false;

            if (imageService.value.getVisible() || imageService2.value.getVisible() || showAerial === 'true') {
                visState = true;
            }

            aerial.value = new TileLayer({
                source: new XYZ({
                    urls: [
                        `https://tile.forestree.dev/tile/${defaults.value.homeCenter.tile}/{z}/{x}/{y}.jpg`
                    ],
                    minZoom: 12,
                    maxZoom: 20,
                    tilePixelRatio: 1
                }),
                visible: visState
            });

            aerialGroup.value = new LayerGroup({
                layers: [
                    aerial.value,
                    labels.value
                ]
            });

            labels.value.setVisible(visState);

            const layers = map.value.getLayers();
            layers.insertAt(0, aerialGroup.value);

        } else {}
    }

    return {
        map,
        mapCreated,
        overlay,
        container,
        content,
        closer,
        filterTreesSource,
        filterTreesLayer,
        selectionSource,
        selectionLayer,
        selectedFeatures,
        polyGeom,
        polygonSource,
        polygonVector,
        modify,
        snap,
        draw,
        polygonAdded,
        polygonWkt,
        aerialGroup,
        imageService,
        imageService2,
        satellite,
        osm,
        labels,
        aerial,
        createMap,
        enablePolygonDraw,
        selectFeatures,
        updateWkt,
        addFeatureToMap,
        restart,
        addTreesGL,
        addImagery,
        clearArea: () => {
            if (polygonSource.value) {
                restart();
            }
        }
    };
};

export default useMapFilter;
